import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { formatMoney, fetchPriceData } from "@theme/theme-currency";
import { getProductPromo } from "@theme/promo-pricing";
import { localizeUrl } from "@theme/localize-url";
import { SearchspringModel } from "@theme/searchspring-model";
import { SearchspringTrackingService } from "@theme/searchspring-tracking-service";
import {
    AUTO_LOAD_BATCH_LIMIT,
    buildAppendedResultsAnnouncement,
    buildRenderableSwatchList,
    buildNextSwatchRequestState,
    buildSwatchA11yState,
    buildSwatchUpdateAnnouncement,
    buildImpressionTrackingKey,
    buildVariantTrackingMapEntry,
    COLOR_FILTER_FIELD,
    DEFAULT_BATCH_SIZE,
    DEFAULT_OOS_SORT_RULE,
    DEFAULT_VARIANT_SORT_OVERRIDE,
    DEFAULT_EXPANSION_RESULTS_PER_PAGE,
    getLoadMoreMode,
    getSearchExpansionConfig,
    hasDisableBatchUrlParam,
    getNextOperableSwatchIndex,
    isSelectedSwatchAvailable,
    isSwatchOperable,
    isSwatchRequestStateCurrent,
    parseSearchspringSwatches,
    shouldAutoLoadNextBatch,
} from "@theme/searchspring-variant-engine";

export default class SearchspringListingBase extends Component {
    // Mapping between user-friendly URL slugs and Searchspring technical fields
    static SORT_URL_MAPPING = {
        "new-arrivals": "ss_days_since_published:asc",
        "top-sellers": "ga_unique_purchases:desc",
        "highest-rated": "mfield_yotpo_reviews_average:desc",
        "price-low": "ss_price:asc",
        "price-high": "ss_price:desc",
        featured: "relevance",
    };

    static SORT_TECHNICAL_TO_URL = Object.fromEntries(
        Object.entries(SearchspringListingBase.SORT_URL_MAPPING).map(([k, v]) => [v, k]),
    );

    // Mapping between technical filter fields and URL-friendly slugs
    static FILTER_URL_MAPPING = {
        "filter.variant_mfield_custom_main_color": "color",
        "filter.variant_mfield_custom_compatible_with": "compatible-with",
        "filter.mfield_custom_device_manufacturer": "brand",
        "filter.variant_mfield_custom_short_features_description": "feature",
        "filter.mfield_custom_parent_product_line": "series",
    };

    static FILTER_URL_TO_TECHNICAL = Object.fromEntries(
        Object.entries(SearchspringListingBase.FILTER_URL_MAPPING).map(([k, v]) => [v, k]),
    );

    /**
     * @param {string} slug - The pretty URL slug
     * @returns {string} The technical field or the original slug if no mapping exists
     */
    static prettySortToTechnical(slug) {
        return SearchspringListingBase.SORT_URL_MAPPING[slug] || slug;
    }

    /**
     * Convert a technical Searchspring field to pretty URL slug
     * @param {string} technical - The technical field (e.g., "price:asc")
     * @returns {string} The pretty slug or the original technical value if no mapping exists
     */
    static technicalSortToPretty(technical) {
        return SearchspringListingBase.SORT_TECHNICAL_TO_URL[technical] || technical;
    }

    /**
     * Convert a technical filter field name to a URL-friendly slug
     * @param {string} field - The technical filter field name
     * @returns {string} The URL slug or the original field name if no mapping exists
     */
    static filterFieldToUrl(field) {
        return SearchspringListingBase.FILTER_URL_MAPPING[field] || field;
    }

    /**
     * Convert a URL-friendly filter slug back to technical field name
     * @param {string} slug - The URL-friendly slug
     * @returns {string} The technical field name or the original slug if no mapping exists
     */
    static urlToFilterField(slug) {
        return SearchspringListingBase.FILTER_URL_TO_TECHNICAL[slug] || slug;
    }

    constructor() {
        super();

        this.STOREFRONT_TOKEN = Theme.otterVars?.storefrontToken || "";
        const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
        const apiDomain = isLocal ? window.Shopify?.shop || "your-store.myshopify.com" : window.location.hostname;

        this.GRAPHQL_URL = `https://${apiDomain}/api/2024-01/graphql.json`;
        this.yotpoWidgetId = Theme.otterVars?.yotpoWidgetId || null;
        this.handleProductClickBound = this.#handleProductClick.bind(this);
        this.hasInitialized = false;
        this.hasAutoLoaded = false;
        this.observer = null;
        this.activeFilters = "";
        this.enableExpansion = true;
        this.resultsPerPage = 100;
        this.oosSortingRule = DEFAULT_OOS_SORT_RULE;
        this.variantSortOverride = DEFAULT_VARIANT_SORT_OVERRIDE;
        this.renderedBannerIndexes = new Set();
        this.autoLoadCount = 0;
        this.autoLoadLimit = AUTO_LOAD_BATCH_LIMIT;

        this.currencySettings = {
            currencyCodeEnabled: window.OtterVars.MarketSettings?.currencyCodeEnabled,
            moneyWithCurrencyFormat: window.OtterVars.MarketSettings?.moneyWithCurrencyFormat,
            moneyWithoutCurrencyFormat: window.OtterVars.MarketSettings?.moneyWithoutCurrencyFormat,
        };

        this.siteId = window.OtterVars.searchspringId;
        this.searchspringModel = new SearchspringModel({ siteId: this.siteId });
        this.searchspringTrackingService = new SearchspringTrackingService({ siteId: this.siteId });
        this.tooltipContainer = null;
        this.activeTooltipTrigger = null;
        this.handleOutsideTooltipClickBound = this.handleOutsideTooltipClick.bind(this);
        this.tooltipData = null;

        this.pageLoadId = crypto.randomUUID?.() || Math.random().toString(36).substring(2);
        this.trackedImpressions = new Set();
        this.productDataMap = new Map();
        this.swatchRequestStateByCard = new WeakMap();
        this.impressionObserver = null;
        this.handleCartUpdateBound = this.#handleCartUpdate.bind(this);
        this.handleSwatchKeydownBound = this.handleSwatchKeydown.bind(this);
    }

    get abortController() {
        return this.searchspringModel?.getState().abortController ?? null;
    }

    set abortController(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setAbortController(value);
    }

    get currentResponseId() {
        return this.searchspringModel?.getState().currentResponseId ?? null;
    }

    set currentResponseId(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setCurrentResponseId(value);
    }

    get currentPage() {
        return this.searchspringModel?.getState().currentPage ?? 1;
    }

    set currentPage(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setCurrentPage(value);
    }

    get renderBuffer() {
        return this.searchspringModel?.getState().renderBuffer ?? [];
    }

    set renderBuffer(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setRenderBuffer(value);
    }

    get totalPages() {
        return this.searchspringModel?.getState().totalPages ?? 0;
    }

    set totalPages(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setTotalPages(value);
    }

    get totalResults() {
        return this.searchspringModel?.getState().totalResults ?? 0;
    }

    set totalResults(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setTotalResults(value);
    }

    get totalInlineBannerCount() {
        return this.searchspringModel?.getState().totalInlineBannerCount ?? 0;
    }

    set totalInlineBannerCount(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setTotalInlineBannerCount(value);
    }

    get knownRenderableResults() {
        return this.searchspringModel?.getState().knownRenderableResults ?? 0;
    }

    set knownRenderableResults(value) {
        if (!this.searchspringModel) return;
        this.searchspringModel.setKnownRenderableResults(value);
    }

    async connectedCallback() {
        super.connectedCallback();

        if (!this.siteId) {
            console.error("Searchspring Error: Missing market's 'site-id' attribute.");
            return;
        }

        this.productGrid = this.querySelector(".js-product-grid-list");
        this.loadMoreBtn = this.querySelector(".js-load-more");
        this.sortingContainer = this.querySelector(".js-sorting-options-container");
        this.sortingTemplate = this.querySelector("#sorting-option-template");
        this.currentSortLabel = this.querySelector(".js-current-sort-label");
        this.productsPerPage = this.dataset.productsPerPage ? parseInt(this.dataset.productsPerPage) : 24;

        this.filterToggleButtons = this.querySelectorAll(".js-facets-togle");
        this.filtersParent = this.querySelector(".js-product-grid-holder");

        if (this.filterToggleButtons.length && this.filtersParent) {
            this.filterToggleButtons.forEach((button) => {
                button.addEventListener("click", () => this.filterToggle(button));
            });
        }

        const tooltipDataElement = document.getElementById("tooltip-data");
        if (tooltipDataElement) {
            this.tooltipData = JSON.parse(tooltipDataElement.innerHTML);
        }
        this.initTooltipContainer();

        this.#initTemplates();
        this.initContext();
        this.shouldCommitSortChange = false;

        await this.initExpansionConfig();

        document.addEventListener("cart:update", this.handleCartUpdateBound);

        if (this.loadMoreBtn) {
            this.loadMoreBtn.addEventListener("click", () => this.loadNextPage());
        }

        this.addEventListener("click", this.handleProductClickBound);

        this.addEventListener("pointerdown", (e) => {
            if (!(e.target instanceof Element)) return;
            const sortOption = e.target.closest('[role="option"]');
            if (!sortOption) return;
            this.shouldCommitSortChange = true;
        });

        this.addEventListener("keydown", (e) => {
            if (!(e.target instanceof Element)) return;
            const sortOption = e.target.closest('[role="option"]');
            if (!sortOption) return;

            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                // Do not let arrow navigation inherit a stale commit flag.
                this.shouldCommitSortChange = false;
                return;
            }

            if (e.key !== "Enter" && e.key !== " ") return;
            this.shouldCommitSortChange = true;

            const input = sortOption.querySelector('input[type="radio"][name="sort_by"]');
            if (!(input instanceof HTMLInputElement)) return;

            input.dataset.commitSortChange = "true";

            // Enter on an already-checked radio may not emit native change.
            // Force a committed change for keyboard selection.
            if (e.key === "Enter") {
                e.preventDefault();
                input.checked = true;
                input.dispatchEvent(new Event("change", { bubbles: true }));
            }
        });

        this.addEventListener("change", (e) => {
            if (e.target.name === "sort_by") {
                if (e.target instanceof HTMLInputElement && e.target.type === "radio") {
                    const hasInputCommit = e.target.dataset.commitSortChange === "true";
                    if (!this.shouldCommitSortChange && !hasInputCommit) return;
                    this.shouldCommitSortChange = false;
                    delete e.target.dataset.commitSortChange;
                }
                this.#handleSortChange(e.target);
            }
        });

        document.addEventListener("theme:compare:update", () => this.updateCompareCheckboxes());
        window.addEventListener("resize", () => this.closeTooltip());
        window.addEventListener("scroll", () => this.closeTooltip(), true);

        await this.#initialFetch();
    }

    disconnectedCallback() {
        this.removeEventListener("click", this.handleProductClickBound);
        if (this.observer) this.observer.disconnect();
        if (this.impressionObserver) this.impressionObserver.disconnect();
        document.removeEventListener("click", this.handleOutsideTooltipClickBound);
        document.removeEventListener("cart:update", this.handleCartUpdateBound);
        if (this.tooltipContainer) this.tooltipContainer.remove();
    }

    initContext() {
        const context = this.searchspringModel.buildContextFromUrl({
            search: window.location.search,
            urlToFilterField: SearchspringListingBase.urlToFilterField,
        });

        this.currentSearchQuery = context.currentSearchQuery;
        this.activeFilters = context.activeFilters;
        this.#setCurrentPage(context.currentPage);
    }

    #buildModelRenderContext() {
        return {
            activeFilters: this.activeFilters,
            currentSearchQuery: this.currentSearchQuery,
            enableExpansion: this.enableExpansion,
            oosSortRule: this.oosSortingRule,
            variantSortOverride: this.variantSortOverride,
            saleColorName: this.saleColorName,
            saleColorId: this.saleColorId,
            displayNonSaleVariants: this.displayNonSaleVariants,
            colorField: COLOR_FILTER_FIELD,
            GRAPHQL_URL: this.GRAPHQL_URL,
            STOREFRONT_TOKEN: this.STOREFRONT_TOKEN,
        };
    }

    #setCurrentPage(page) {
        this.searchspringModel.setCurrentPage(page);
    }

    async initExpansionConfig() {
        // Fetch the global config that Liquid injected into window.OtterVars
        const config = getSearchExpansionConfig();

        // Sync the base class state with the global config
        this.enableExpansion = config.enableExpansion;
        this.resultsPerPage = config.resultsPerPage;
        this.oosSortingRule = config.oosSortRule;
        this.variantSortOverride = config.variantSortOverride;
        this.saleColorName = config.saleColorName;
        this.saleColorId = config.saleColorId;
        this.displayNonSaleVariants = config.displayNonSaleVariants;
    }

    shouldPushCollectionViewItemList() {
        return false;
    }

    getVariantIdFromUrl(url) {
        if (!url) return "";

        try {
            return new URL(url, window.location.origin).searchParams.get("variant") || "";
        } catch (_error) {
            return "";
        }
    }

    getInitialSwatch(product, preferredVariantId = "") {
        return this.searchspringModel.getInitialSwatch(product, {
            preferredVariantId,
            activeFilters: this.activeFilters,
            currentSearchQuery: this.currentSearchQuery,
            colorField: COLOR_FILTER_FIELD,
        });
    }

    async getRenderableResults(results = []) {
        return this.searchspringModel.buildRenderableResults(results, this.#buildModelRenderContext());
    }

    getVisibleParentProductCount() {
        if (!this.productGrid) return 0;

        const parentProductIds = Array.from(this.productGrid.querySelectorAll("product-card"))
            .map((card) => card.getAttribute("data-product-id"))
            .filter(Boolean);

        return new Set(parentProductIds).size;
    }

    getVisibleRenderableProductCount() {
        return this.productGrid?.querySelectorAll("product-card").length || 0;
    }

    resetBatchRenderState() {
        this.searchspringModel.resetBatchState();
        this.renderedBannerIndexes.clear();
        this.autoLoadCount = 0;

        if (this.observer) {
            this.observer.disconnect();
        }
    }

    async appendDataToRenderBuffer(data) {
        const renderItems = await this.searchspringModel.appendPageData(data, this.#buildModelRenderContext());

        return renderItems;
    }

    async getNextRenderBatch() {
        const batch = await this.searchspringModel.takeNextRenderBatch({
            batchSize: DEFAULT_BATCH_SIZE,
            requestOptions: this.getSearchspringRequestParams(),
            renderContext: this.#buildModelRenderContext(),
        });

        return batch;
    }

    hasMoreRenderableResults() {
        return this.searchspringModel.hasMoreRenderableResults();
    }

    updateLoadMoreVisibility() {
        if (!this.loadMoreBtn) return;

        const loadMode = getLoadMoreMode({
            hasMoreResults: this.hasMoreRenderableResults(),
            autoLoadCount: this.autoLoadCount,
            autoLoadLimit: this.autoLoadLimit,
        });

        if (loadMode) {
            this.loadMoreBtn.classList.remove("hidden");
            this.loadMoreBtn.setAttribute("data-load-mode", loadMode);
            return;
        }

        this.loadMoreBtn.classList.add("hidden");
        this.loadMoreBtn.removeAttribute("data-load-mode");
    }

    async renderBufferedResults() {
        while (this.hasMoreRenderableResults()) {
            const batch = this.searchspringModel.takeBufferedBatch(DEFAULT_BATCH_SIZE);

            if (batch.length === 0) {
                break;
            }

            await this.renderProductBatch(batch, true);
        }
    }

    async renderBufferedResultsThroughPage(maxSourcePage = 1) {
        while (this.hasMoreRenderableResults()) {
            const batch = this.searchspringModel.takeBufferedBatchThroughPage(maxSourcePage, DEFAULT_BATCH_SIZE);

            if (batch.length === 0) {
                break;
            }

            await this.renderProductBatch(batch, true);
        }
    }

    getEndpoint() {
        throw new Error("getEndpoint must be implemented");
    }

    getBeaconPath() {
        return 'search';
    }

    isBatchingDisabled(search = window.location.search) {
        return this.enableExpansion === true && hasDisableBatchUrlParam(search);
    }

    getBaseParams(page) {
        return {
            siteId: this.siteId ?? "",
            page: page.toString(),
            resultsPerPage: this.enableExpansion === true
                ? String(this.resultsPerPage ?? DEFAULT_EXPANSION_RESULTS_PER_PAGE)
                : (this.productsPerPage?.toString() ?? "24"),
            resultsFormat: "json",
            domain: window.location.href,
            userId: this.#getOrSetCookie("ssUserId"),
            sessionId: this.#getOrSetCookie("ssSessionId"),
            pageLoadId: this.pageLoadId,
            beacon: "true",
            test: "true",
        };
    }

    getSearchspringRequestParams(page = 1) {
        const disableBatching = this.isBatchingDisabled();

        return {
            siteId: this.siteId,
            endpoint: this.getEndpoint(),
            page,
            baseParams: this.getBaseParams(page),
            activeFilters: this.activeFilters,
            search: window.location.search,
            prettySortToTechnical: SearchspringListingBase.prettySortToTechnical,
            enableExpansion: this.enableExpansion,
            disableBatching,
            onBeforeRequest: this.hasInitialized ? () => this.startUpdating() : null,
        };
    }

    decodeHtml(html) {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
    }

    /*
    initialFetch 
    <pseudocode> 
        save requested page
        reset UI/buffer to page 1
        fetch page 1

        if Searchspring says redirect:
        beacon + navigate away

        if query should become a color filter:
            rewrite filters + URL
            refetch page 1
            render filtered page
            finish init
        return

        render first page

        if user landed on page > 1:
        prefetch pages 2..N and append them

        mark initialized
        fire analytics if needed
        enable infinite scroll
    </pseudocode>

    this method is not “load the current page directly.” It is “establish canonical first-page state, then catch up.”
    That makes the render buffer, filters, analytics, and infinite-scroll state consistent from the start.
    */

    async #initialFetch() {
        const initialPage = this.currentPage;
        const shouldPushInitialCollectionViewItemList = initialPage === 1 && this.shouldPushCollectionViewItemList();
        this.#setCurrentPage(1);
        this.resetBatchRenderState();
        this.#setCurrentPage(1);

        const firstPageData = await this.searchspringModel.getProductsData(1, this.getSearchspringRequestParams(1));
        if (!firstPageData) return;

        if (firstPageData.merchandising?.redirect) {
            this.#sendBeaconEvent("redirect", {
                responseId: this.currentResponseId,
                redirect: firstPageData.merchandising.redirect,
            });
            window.location.href = firstPageData.merchandising.redirect;
            return;
        }

        const colorFacetField = COLOR_FILTER_FIELD;

        const redirectedFilters = this.searchspringModel.resolveQueryColorRedirect({
            data: firstPageData,
            currentSearchQuery: this.currentSearchQuery,
            activeFilters: this.activeFilters,
            colorFacetField,
        });

        if (redirectedFilters) {
            this.activeFilters = redirectedFilters;
            this.updateBrowserURL();

            const filteredData = await this.searchspringModel.getProductsData(1, this.getSearchspringRequestParams(1));
            if (filteredData) {
                await this.#renderFullPage(filteredData);
                this.hasInitialized = true;
                this.#setupInfiniteScroll();
                return;
            }
        }

        await this.#renderFullPage(firstPageData);

        if (initialPage > 1) {
            if (this.enableExpansion === true && this.currentPage >= initialPage) {
                await this.renderBufferedResultsThroughPage(initialPage);
                this.autoLoadCount = this.autoLoadLimit;
                this.updateLoadMoreVisibility();
                this.hasAutoLoaded = true;
            } else {
                await this.#prefetchDeepPageResults(this.currentPage, initialPage);
            }
        }

        this.hasInitialized = true;

        if (shouldPushInitialCollectionViewItemList && firstPageData) {
            this.pushViewItemListToDataLayer(firstPageData);
        }

        if (this.currentPage >= 1) {
            this.#setupInfiniteScroll();
        }
    }

    /**
     * Renders a complete page of results: products, filters, active filters, sorting
     * options, and fires the 'render' beacon event.
     * Used by both the initial fetch and the color-matched query branch in #initialFetch.
     * @param {Object} data - Searchspring API response
     */
    async #renderFullPage(data) {
        await this.renderProducts(data, false);
        this.renderFilters(data);
        this.renderActiveFilters(data);
        this.renderSorting(data);
        this.#sendBeaconEvent("render", { responseId: this.currentResponseId });
    }

    /**
     * Prefetches pages fromPage+1…toPage sequentially when the user lands on a
     * deep-linked page (e.g. ?page=3). Appends all results to the render buffer,
     * flushes them to the DOM, then marks auto-load as exhausted and refreshes
     * the load-more button visibility.
     * @param {number} fromPage - The page already rendered (after #renderFullPage)
     * @param {number} toPage - The target page number from the URL on initial load
     */
    async #prefetchDeepPageResults(fromPage, toPage) {
        await this.searchspringModel.prefetchPages({
            fromPage,
            toPage,
            requestOptions: this.getSearchspringRequestParams(),
            renderContext: this.#buildModelRenderContext(),
        });

        await this.renderBufferedResults();
        this.autoLoadCount = this.autoLoadLimit;
        this.updateLoadMoreVisibility();
        this.hasAutoLoaded = true;
    }

    #initTemplates() {
        this.cardTemplate = document.getElementById("product-card-template");
        this.facetAccordionTemplate = document.getElementById("facet-accordion-template");
        this.facetCheckboxTemplate = document.getElementById("facet-checkbox-template");
    }

    filterToggle(button) {
        if (!this.filtersParent || !button) return;

        const isMobile = window.innerWidth < 990;

        if (isMobile) {
            this.filtersParent.classList.toggle("filters-mobile-hidden");
            document.querySelector("html").classList.toggle("mobile-filters-visible");
            this.filtersParent.classList.remove("filters-hidden");

            if (this.filtersParent.classList.contains("filters-mobile-hidden")) {
                removeTrapFocus(button);
            } else {
                trapFocus(this.filtersParent.querySelector(".facets-block-wrapper"));
            }
        } else {
            removeTrapFocus(button);
            this.filtersParent.classList.toggle("filters-hidden");
            this.filtersParent.classList.add("filters-mobile-hidden");
            button.classList.toggle("is-active");
        }
    }

    renderSorting(data) {
        const container = this.querySelector(".js-sorting-options-container");
        const template = document.getElementById("sorting-option-template");
        const statusLabel = this.querySelector(".js-sorting-status");

        if (!container || !template || !data.sorting?.options?.length) return;

        container.innerHTML = "";

        const options = data.sorting.options;
        const currentSort = new URLSearchParams(window.location.search).get("sort_by");

        let selectedValue = currentSort;

        if (selectedValue) {
            selectedValue = SearchspringListingBase.prettySortToTechnical(selectedValue);
        }

        if (!selectedValue) {
            const relevanceOption = options.find((o) => o.field === "relevance");

            if (relevanceOption) {
                selectedValue = `${relevanceOption.field}:${relevanceOption.direction}`;
            } else {
                const first = options[0];
                selectedValue = `${first.field}:${first.direction}`;
            }
        }

        options.forEach((option) => {
            const clone = template.content.cloneNode(true);
            const input = clone.querySelector("input");
            const label = clone.querySelector(".sorting-filter__label");

            if (!input || !label) return;

            const sortValue = `${option.field}:${option.direction}`;

            input.value = sortValue;
            input.id = `sort-${option.field}-${option.direction}`;
            label.textContent = option.label;

            if (sortValue === selectedValue) {
                input.checked = true;
                if (statusLabel) statusLabel.textContent = option.label;
            }

            container.appendChild(clone);
        });
    }

    async #handleSortChange(input) {
        const sortValue = input.value;
        this.#setCurrentPage(1);
        this.hasAutoLoaded = false;
        this.resetBatchRenderState();
        this.#setCurrentPage(1);
        const nextUrl = this.searchspringModel.buildSortUrl({
            href: window.location.href,
            sortValue,
            technicalSortToPretty: SearchspringListingBase.technicalSortToPretty,
        });

        window.history.pushState({}, "", nextUrl);
        input.closest("details").removeAttribute("open");

        try {
            await this.#fetchAndRenderPage(1, { withSorting: true });
        } catch (error) {
            if (error.name !== "AbortError") {
                console.error("Searchspring Sort Error:", error);
                this.stopUpdating();
            }
        }
    }

    #handleProductClick(e) {
        const card = e.target.closest("product-card");
        if (!card || !this.currentResponseId) return;

        const result = this.searchspringTrackingService.buildTrackingResultFromElement(card);

        if (!result) return;

        this.#sendBeaconEvent(
            "clickthrough",
            {
                responseId: this.currentResponseId,
                results: [
                    {
                        type: "product",
                        parentId: result.uid, // use the same 'uid' as parentId is not available in the search response data https://docs.searchspring.com/reference/get-search#response-schemas
                        uid: result.uid,
                        ...(result.sku ? { sku: result.sku } : {}),
                    },
                ],
            },
            true,
        );
    }

    updateCompareCheckboxes() {
        const storageProducts = JSON.parse(localStorage.getItem("compareProducts") || "[]");
        const comparedVariantIds = new Set(storageProducts.map((product) => String(product.id || "")).filter(Boolean));
        const comparedHandles = new Set(storageProducts.map((p) => p.handle).filter(Boolean));

        this.querySelectorAll(".js-compare-checkbox").forEach((checkbox) => {
            const productHandle = checkbox.getAttribute("data-product-handle");
            const variantId = checkbox.closest("product-card")?.getAttribute("data-variant-id");
            const input = checkbox.querySelector("input");
            const isCompared = variantId ? comparedVariantIds.has(String(variantId)) : comparedHandles.has(productHandle);

            if (isCompared) {
                checkbox.classList.add("is-checked");
                if (input) input.checked = true;
            } else {
                checkbox.classList.remove("is-checked");
                if (input) input.checked = false;
            }
        });
    }

    isCached(src) {
        if (!src) return false;
        const img = new Image();
        img.src = src;

        return img.complete && img.naturalWidth !== 0;
    }

    /**
     * Push view_item_list event to dataLayer for collection pages
     * Works with SearchSpring data
     * @param {Object} data - SearchSpring API response
     */
    pushViewItemListToDataLayer(data) {
        if (!data || !data.results || data.results.length === 0) return;

        const itemsCount = this.productsPerPage || 24;

        try {
            const results = data.results.slice(0, itemsCount);
            const productData = {
                ids: [],
                names: [],
                skus: [],
                unitPrices: [],
                discountedPrices: [],
                colors: [],
                brands: [],
                promos: [],
                levelDiscounts: [],
            };

            results.forEach((product) => {
                const entry = this.#buildDataLayerProductEntry(product);
                productData.ids.push(entry.id);
                productData.names.push(entry.name);
                productData.skus.push(entry.sku);
                productData.unitPrices.push(entry.unitPrice);
                productData.discountedPrices.push(entry.discountedPrice);
                productData.colors.push(entry.color);
                productData.brands.push(entry.brand);
                productData.promos.push("");
                productData.levelDiscounts.push("0.00");
            });

            const collectionId = this.dataset.collectionId || "";
            const collectionTitle = this.dataset.collectionTitle || document.title.replace(" – OtterBox", "").trim();
            const listPayload = {
                ga_tealium_event: "view_item_list",
                tealium_event: "view_item_list",
                page_category: "listing page",
                item_list_id: collectionId,
                item_list_name: collectionTitle,
                product_id: productData.ids,
                product_name: productData.names,
                product_sku: productData.skus,
                product_unit_price: productData.unitPrices,
                product_discounted_unit_price: productData.discountedPrices,
                product_color: productData.colors,
                product_brand: productData.brands,
                product_coupon_promo: productData.promos,
                product_level_discount: productData.levelDiscounts,
            };

            if (typeof window !== "undefined") {
                if (window.utag && window.utag.link) {
                    window.utag.link(listPayload);
                }

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push(Object.assign({ event: "view_item_list" }, listPayload));
            }
        } catch (_error) { }
    }

    /**
     * Maps a single Searchspring product object to the flat shape expected by the
     * view_item_list dataLayer / Tealium payload used in pushViewItemListToDataLayer.
     * @param {Object} product - A Searchspring result product
     * @returns {{ id: string, name: string, sku: string, unitPrice: string, discountedPrice: string, color: string, brand: string }}
     */
    #buildDataLayerProductEntry(product) {
        const msrp = product.msrp || product.price;
        let color = "";

        if (product.ss_swatches) {
            try {
                const swatches = parseSearchspringSwatches(product.ss_swatches);
                const firstSwatch = swatches.find((swatch) => swatch.available > 0) || swatches[0];

                if (firstSwatch) color = firstSwatch.color || "";
            } catch (_error) { }
        }

        return {
            id: String(product.uid || product.sku || ""),
            name: (product.name || product.title || "").replace(/&amp;/g, "&"),
            sku: String(product.sku || product.uid || ""),
            unitPrice: parseFloat(msrp || 0).toFixed(2),
            discountedPrice: parseFloat(product.price || 0).toFixed(2),
            color,
            brand: product.brand || product.vendor || "",
        };
    }

    async renderProducts(data, append = false) {
        if (!this.productGrid || !this.cardTemplate) return;

        this.classList.remove("is-loaded");

        if (!append) {
            this.clearNoResultsMessage();
            this.productGrid.innerHTML = "";
            this.trackedImpressions.clear();
            this.productDataMap.clear();
            this.resetBatchRenderState();
            this.#setCurrentPage(data?.pagination?.currentPage || 1);
        }

        await this.appendDataToRenderBuffer(data);

        const batchItems = await this.getNextRenderBatch();

        if (batchItems.length === 0) {
            if (!append) this.handleNoResults();
            this.updateLoadMoreVisibility();
            this.stopUpdating();
            return;
        }

        await this.renderProductBatch(batchItems, append);
    }

    postRenderProductBatch(appendedCards = [], append = false) { }

    async renderProductBatch(batchItems, append = false) {
        if (!this.productGrid || !this.cardTemplate) return [];

        const swatchLimit = window.innerWidth < 750 ? 3 : 5;
        const appendedCards = [];
        const visibleRankOffset = append ? this.productGrid.querySelectorAll("product-card").length : 0;

        batchItems.forEach((renderItem, index) => {
            const product = renderItem.parentProduct;
            const bannerIndex = renderItem.renderOrderIndex ?? index;
            const inlineContent = renderItem.inlineContent || [];
            const banner =
                renderItem.sourcePage === 1
                    ? inlineContent.find((content) => content.config?.position?.index === bannerIndex)
                    : null;

            if (banner && !this.renderedBannerIndexes.has(bannerIndex)) {
                this.#injectInlineContent(banner);
                this.renderedBannerIndexes.add(bannerIndex);
            }

            if (!this.cardTemplate) return;

            const renderData = this.#resolveProductRenderData(renderItem, swatchLimit);

            if (!renderData.isProductAvailable && !this.enableExpansion) return;

            const clone = this.#buildProductCardClone(renderData);
            this.#hydrateCardContent(clone, renderData);

            const cardElement = clone.querySelector("product-card");

            if (cardElement) {
                this.#setCardTrackingAttributes(
                    cardElement,
                    renderItem,
                    renderData.variantId,
                    renderData.isProductAvailable,
                    visibleRankOffset + index + 1,
                );
            }

            const initialTrackingState = buildVariantTrackingMapEntry(
                product,
                renderItem,
                renderData.variantId,
                renderData.liveData,
            );

            if (initialTrackingState) {
                this.productDataMap.set(initialTrackingState.variantId, initialTrackingState.entry);
            }

            this.productGrid?.appendChild(clone);

            const card = this.productGrid.lastElementChild;

            if (card) {
                appendedCards.push(card);
            }

            this.#attachSwatchEventListeners(card, product, renderItem);
        });

        this.updateLoadMoreVisibility();

        const totalVisibleProducts = this.getVisibleRenderableProductCount();
        const adjustedTotal = Math.max(this.knownRenderableResults, totalVisibleProducts);
        this.fillOrHide(this, ".js-total-product-results", `${adjustedTotal}`);
        this.fillOrHide(this, ".js-visible-results", `${totalVisibleProducts}`);

        this.postRenderProductBatch(appendedCards, append);

        this.stopUpdating();
        this.classList.add("is-loaded");

        this.updateCompareCheckboxes();
        document.dispatchEvent(new CustomEvent("theme:compare:update"));
        this.refreshYotpo();
        this.#setupImpressionTracking();

        return appendedCards;
    }

    /**
     * Computes all render-time values for a single product render item.
     * Resolves the active swatch, derives availability from live price data,
     * and generates all markup fragments (badge, swatches, rating, price, promo).
     * Returns a plain data object consumed by #buildProductCardClone and #hydrateCardContent.
     * @param {Object} renderItem - A single item from the render buffer
     * @param {number} swatchLimit - Maximum number of swatches to display
     * @returns {Object} Resolved render data for stamping and hydrating the card
     */
    #resolveProductRenderData(renderItem, swatchLimit) {
        const product = renderItem.parentProduct;

        let allSwatches = [];

        try {
            allSwatches = parseSearchspringSwatches(product.ss_swatches);
        } catch (error) {
            console.error("Error parsing swatches", error);
        }

        const availableSwatches = allSwatches.filter((swatch) => swatch.available > 0);
        const hasOptions = availableSwatches.length > 1;
        const initialSwatch = renderItem.swatch || this.getInitialSwatch(product, renderItem.variantId);
        const variantId = this.getVariantIdFromUrl(initialSwatch.url) || String(renderItem.variantId || "");
        const swatchPrice = initialSwatch?.price;
        const liveData =
            swatchPrice != null
                ? {
                    price: { amount: String(swatchPrice) },
                    compareAtPrice:
                        initialSwatch.compare_at_price != null ? { amount: String(initialSwatch.compare_at_price) } : null,
                    availableForSale: Boolean(renderItem.available),
                    isGwp: renderItem.isGwp ? { value: "true" } : null,
                }
                : null;
        const isExpandedVariantAvailable = isSelectedSwatchAvailable(renderItem.available, liveData);
        const isProductAvailable = this.enableExpansion
            ? isExpandedVariantAvailable
            : product.ss_available === "0" || !liveData || liveData.availableForSale === false
                ? false
                : true;

        const productType = product.product_type || "";
        const productId = product.uid || product.sku;
        const productUrl = localizeUrl(initialSwatch.url || product.url);
        const productHandle = new URL(productUrl, window.location.origin).pathname.split("/products/")[1];
        const imageUrl = initialSwatch.image || product.imageUrl;
        const badgeHtml = this.generateBadgeMarkup(product, initialSwatch, renderItem);
        const swatchesHtml = this.generateSwatchesMarkup(product, swatchLimit, variantId);
        const ratingHtml = this.generateRatingMarkup(product);
        const priceHtml = this.generateLivePriceMarkup(this.#applyCampaignPricing(liveData, product.tags, this.#promoCollectionIds(liveData, renderItem), product.uid));
        const promoHtml = this.generatePromoMarkup(product);

        const tempSwatchesDiv = document.createElement("div");

        tempSwatchesDiv.innerHTML = swatchesHtml;

        const activeSwatch = tempSwatchesDiv.querySelector(".is-active");
        const rawHover = activeSwatch ? activeSwatch.dataset.variantHoverImage : product.imageHoverUrl || product.imageUrl;
        const hoverImageUrl = rawHover === "null" || !rawHover ? imageUrl : rawHover;
        const productTitle = (product.name || product.title).replace(/&amp;/g, "&");
        const mainImageSrcset = this.generateSrcset(imageUrl);
        const hoverImageSrcset = this.generateSrcset(hoverImageUrl);

        return {
            product,
            renderItem,
            variantId,
            liveData,
            isProductAvailable,
            hasOptions,
            productType,
            productId,
            productUrl,
            productHandle,
            imageUrl,
            hoverImageUrl,
            badgeHtml,
            swatchesHtml,
            ratingHtml,
            priceHtml,
            promoHtml,
            activeSwatch,
            productTitle,
            mainImageSrcset,
            hoverImageSrcset,
            initialSwatch,
        };
    }

    /**
     * Clones the product-card template and performs scalar token replacement
     * (IDs, URLs, image URLs, product title). Does not fill dynamic HTML sections —
     * that is handled by #hydrateCardContent on the returned clone.
     * @param {Object} renderData - Resolved render data from #resolveProductRenderData
     * @returns {Element} Cloned DOM element ready for content hydration
     */
    #buildProductCardClone(renderData) {
        const {
            productId,
            variantId,
            productType,
            productUrl,
            productHandle,
            imageUrl,
            mainImageSrcset,
            hoverImageUrl,
            hoverImageSrcset,
            productTitle,
        } = renderData;

        let html = this.cardTemplate.innerHTML;

        html = html
            .replace(/\${productId}/g, productId)
            .replace(/\${variantId}/g, variantId)
            .replace(/\${productType}/g, productType)
            .replace(/\${productUrl}/g, productUrl)
            .replace(/\${productHandle}/g, productHandle)
            .replace(/\${imageUrl}/g, imageUrl)
            .replace(/\${mainImageSrcset}/g, mainImageSrcset)
            .replace(/\${hoverImageUrl}/g, hoverImageUrl)
            .replace(/\${hoverImageSrcset}/g, hoverImageSrcset)
            .replace(/\${productTitle}/g, productTitle);

        const tempDiv = document.createElement("div");

        tempDiv.innerHTML = html;

        return tempDiv.firstElementChild;
    }

    /**
     * Fills a product card clone with its dynamic HTML sections: badge, swatches,
     * rating, price, promo, title link, main/hover images and tracking links.
     * Called on the clone returned by #buildProductCardClone, before it is appended to the grid.
     * @param {Element} clone - Card clone produced by #buildProductCardClone
     * @param {Object} renderData - Resolved render data from #resolveProductRenderData
     */
    #hydrateCardContent(clone, renderData) {
        const {
            product,
            productUrl,
            hasOptions,
            badgeHtml,
            swatchesHtml,
            ratingHtml,
            priceHtml,
            promoHtml,
            activeSwatch,
            imageUrl,
        } = renderData;

        const quickAdd = clone.querySelector("quick-add-component");

        if (quickAdd) {
            quickAdd.classList.add(hasOptions ? "is-multiple-variants" : "is-single-variant");
        }

        clone.querySelectorAll(".product-card-item__badge-wrapper").forEach((badgeWrapper) => {
            badgeWrapper.innerHTML = badgeHtml;
        });
        this.fillOrHide(clone, ".product-card-item__swatches", swatchesHtml);
        this.fillOrHide(clone, ".product-card-item__rating", ratingHtml);
        this.fillOrHide(clone, ".price-container", priceHtml);
        this.fillOrHide(clone, ".product-card-item__promo", promoHtml);

        const titleLink = clone.querySelector(".product-card-item__title a");

        if (titleLink) {
            titleLink.textContent = (product.name || product.title).replace(/&amp;/g, "&");
            titleLink.href = productUrl;
        }

        const img = clone.querySelector(".product-card-item__image .product-card-item__main-image");
        const imgHover = clone.querySelector(".product-card-item__image .product-card-item__hover-image");

        if (img && imgHover) {
            let imageSrc = activeSwatch ? activeSwatch.dataset.variantImage : product.imageUrl || product.thumbnailImageUrl;
            const currentRawHover = activeSwatch
                ? activeSwatch.dataset.variantHoverImage
                : product.imageHoverUrl || product.imageUrl;
            let imageHoverSrc = currentRawHover === "null" || !currentRawHover ? imageSrc : currentRawHover;
            let imageAlt = (product.name || product.title).replace(/&amp;/g, "&");

            if (imageSrc === "null" || !imageSrc) {
                imageSrc = img.getAttribute("data-placeholder-src");
                imageAlt = img.getAttribute("data-placeholder-alt") || "";
            }

            if (this.isCached(imageSrc)) {
                img.classList.add("is-loaded");
            } else {
                img.onload = () => img.classList.add("is-loaded");
            }

            img.src = imageSrc;
            img.srcset = this.generateSrcset(imageSrc);
            img.alt = imageAlt;

            if (this.isCached(imageHoverSrc)) {
                imgHover.classList.add("is-loaded");
            } else {
                imgHover.onload = () => imgHover.classList.add("is-loaded");
            }

            imgHover.src = imageHoverSrc;
            imgHover.srcset = this.generateSrcset(imageHoverSrc);
            imgHover.alt = imageAlt;
        }

        const links = clone.querySelectorAll(".js-ss-track");
        const newUrl = localizeUrl(activeSwatch ? activeSwatch.dataset.variantUrl : product.url);

        links.forEach((link) => {
            link.href = newUrl;
        });
    }

    /**
     * Sets all data-* tracking and identification attributes on the inner product-card
     * element (data-product-id, data-variant-id, data-ssrank, data-ssquery, etc.)
     * and syncs the card's visual availability state.
     * @param {Element} cardElement - The product-card custom element inside the clone
     * @param {Object} renderItem - The source render item
     * @param {string} variantId - Resolved Shopify variant ID
     * @param {boolean} isProductAvailable - Whether the product is purchasable
     * @param {number} rank - 1-based position in the visible result set (written to data-ssrank)
     */
    #setCardTrackingAttributes(cardElement, renderItem, variantId, isProductAvailable, rank) {
        const product = renderItem.parentProduct;
        const impressionKey = buildImpressionTrackingKey(renderItem);

        cardElement.setAttribute("data-is-data", product.intellisuggestData);
        cardElement.setAttribute("data-is-signature", product.intellisuggestSignature);
        cardElement.setAttribute("data-product-id", product.uid || product.sku);
        cardElement.setAttribute("data-variant-id", variantId);
        cardElement.setAttribute("data-variant-sku", renderItem.sku || product.sku || "");
        cardElement.setAttribute("data-impression-key", impressionKey);
        cardElement.setAttribute("data-ssproduct", variantId || product.uid || product.sku);
        cardElement.setAttribute("data-ssrank", rank);

        if (this.currentSearchQuery) {
            cardElement.setAttribute("data-ssquery", this.currentSearchQuery);
        }

        this.syncCardAvailabilityState(cardElement, isProductAvailable);
    }

    /**
     * Wires keydown (keyboard navigation via handleSwatchKeydownBound) and click
     * (swatch selection + async live-price fetch via #handleSwatchClick) event
     * listeners onto every swatch button inside a newly rendered card.
     * @param {Element} card - The rendered card element (li) just appended to the grid
     * @param {Object} product - The parent product object
     * @param {Object} renderItem - The source render item
     */
    #attachSwatchEventListeners(card, product, renderItem) {
        card.querySelectorAll(".js-swatch-item").forEach((swatch) => {
            swatch.addEventListener("keydown", this.handleSwatchKeydownBound);
            swatch.addEventListener("click", (event) => this.#handleSwatchClick(event, swatch, card, product, renderItem));
        });
    }

    /**
     * Handles a swatch button click: updates all card DOM state (variant ID attrs,
     * images, tracking links, compare-checkbox, form inputs, availability class),
     * then fetches live price data for the selected variant asynchronously.
     * Uses swatchRequestStateByCard to discard stale responses when the user clicks
     * a different swatch before the previous fetch resolves (optimistic concurrency).
     * @param {MouseEvent} event - The originating click event
     * @param {Element} swatch - The swatch button that was clicked
     * @param {Element} card - The rendered card element containing the swatch
     * @param {Object} product - The parent product object
     * @param {Object} renderItem - The source render item
     */
    async #handleSwatchClick(event, swatch, card, product, renderItem) {
        if (!isSwatchOperable(swatch)) {
            event.preventDefault();
            return;
        }

        const swUrl = localizeUrl(swatch.dataset.variantUrl);
        const newImage = swatch.dataset.variantImage;
        let newHoverImage = swatch.dataset.variantHoverImage;
        const tempUrl = new URL(swUrl, window.location.origin);
        const newVariantId = tempUrl.searchParams.get("variant");
        const newProductHandle = tempUrl.pathname.split("/products/")[1];
        const productCard = card.querySelector("product-card");
        const cardItem = card.querySelector(".product-card-item");
        const swatchButtons = card.querySelectorAll(".js-swatch-item");
        const isSelectedSwatchInStock = swatch.dataset.variantAvailable === "true";
        let selectedSwatchAvailable = isSelectedSwatchAvailable(isSelectedSwatchInStock);
        const swatchRequestState = buildNextSwatchRequestState(this.swatchRequestStateByCard.get(card), newVariantId);

        const formVariantInput = card.querySelector('input[name="id"]');
        const productForm = card.querySelector("product-form-component");
        const atcContainer = card.querySelector("add-to-cart-component");
        const compareCheck = card.querySelector(".js-compare-checkbox");

        this.swatchRequestStateByCard.set(card, swatchRequestState);

        if (formVariantInput) formVariantInput.value = newVariantId;
        cardItem?.setAttribute("data-variant-id", newVariantId);
        productCard?.setAttribute("data-variant-id", newVariantId);
        productCard?.setAttribute("data-variant-sku", swatch.dataset.variantSku || product.sku || "");
        productCard?.setAttribute("data-ssproduct", newVariantId || product.uid || product.sku);

        if (compareCheck) {
            const originalProductHandle = compareCheck.dataset.productHandle;
            const wasChecked = compareCheck.classList.contains("is-checked");

            compareCheck.setAttribute("data-product-url", swUrl);
            compareCheck.setAttribute("data-product-handle", newProductHandle);

            if (wasChecked) {
                document.dispatchEvent(
                    new CustomEvent("theme:compare:swatch-change", {
                        detail: {
                            originalProductHandle,
                            newProductURL: swUrl,
                        },
                    }),
                );
            }
        }

        if (productForm) productForm.setAttribute("data-product-url", swUrl);
        if (atcContainer) atcContainer.setAttribute("data-product-variant-media", newImage);

        this.updateSwatchSelectionState(swatchButtons, swatch);
        this.syncCardAvailabilityState(productCard, selectedSwatchAvailable);

        const mainImg = card.querySelector(".product-card-item__image .product-card-item__main-image");
        const hoverImg = card.querySelector(".product-card-item__image .product-card-item__hover-image");
        const cardLinks = card.querySelectorAll(".js-ss-track");

        if (mainImg && newImage) {
            mainImg.src = newImage;
            mainImg.srcset = this.generateSrcset(newImage);
        }

        if (newHoverImage === "null" || newHoverImage === null || !newHoverImage) {
            newHoverImage = newImage;
        }

        if (hoverImg && newHoverImage) {
            hoverImg.src = newHoverImage;
            hoverImg.srcset = this.generateSrcset(newHoverImage);
        }

        cardLinks.forEach((link) => {
            const url = new URL(link.href);
            url.searchParams.set("variant", newVariantId);
            link.href = url.toString();
        });

        if (newVariantId) {
            const swatchTrackingState = buildVariantTrackingMapEntry(product, renderItem, newVariantId);

            if (swatchTrackingState) {
                this.productDataMap.set(swatchTrackingState.variantId, swatchTrackingState.entry);
            }

            try {
                const swatchLivePriceData = await fetchPriceData([`gid://shopify/ProductVariant/${newVariantId}`], {
                    GRAPHQL_URL: this.GRAPHQL_URL,
                    STOREFRONT_TOKEN: this.STOREFRONT_TOKEN,
                    includePromoContext: true,
                });
                const nextLiveData = swatchLivePriceData?.nodes?.[0] || null;

                if (
                    !isSwatchRequestStateCurrent(this.swatchRequestStateByCard.get(card), swatchRequestState.token, newVariantId)
                ) {
                    return;
                }

                const priceContainer = card.querySelector(".price-container");

                if (priceContainer && nextLiveData) {
                    priceContainer.innerHTML = this.generateLivePriceMarkup(
                        this.#applyCampaignPricing(nextLiveData, product.tags, this.#promoCollectionIds(nextLiveData, renderItem), product.uid)
                    );

                    const hydratedTrackingState = buildVariantTrackingMapEntry(product, renderItem, newVariantId, nextLiveData);

                    if (hydratedTrackingState) {
                        this.productDataMap.set(hydratedTrackingState.variantId, hydratedTrackingState.entry);
                    }
                }

                if (nextLiveData) {

                    if (renderItem.swatch) {
                        renderItem.swatch.variant_mfield_custom_page_views = nextLiveData.pageViews?.value;
                    } else {
                        renderItem.variant_mfield_custom_page_views = nextLiveData.pageViews?.value;
                    }

                    const badgeWrapper = card.querySelector(".product-card-item__badge-wrapper");
                    if (badgeWrapper) {
                        badgeWrapper.innerHTML = this.generateBadgeMarkup(product, renderItem.swatch, renderItem);
                    }
                }

                selectedSwatchAvailable = isSelectedSwatchAvailable(isSelectedSwatchInStock, nextLiveData);
                this.syncCardAvailabilityState(productCard, selectedSwatchAvailable);
            } catch (error) {
                console.error("Error fetching live price for swatch:", error);
            }
        }

        if (!isSwatchRequestStateCurrent(this.swatchRequestStateByCard.get(card), swatchRequestState.token, newVariantId)) {
            return;
        }

        this.announceSwatchUpdate(
            card,
            buildSwatchUpdateAnnouncement({
                color: swatch.dataset.colorLabel,
                priceText: this.getPriceAnnouncementText(card.querySelector(".price-container")),
                isAvailable: selectedSwatchAvailable,
            }),
        );
    }

    #injectInlineContent(content) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = content.value;
        const scriptTag = tempDiv.querySelector("script");

        if (!scriptTag) return;

        const rawHtml = scriptTag.getAttribute("data-banner-html");
        const decodedHtml = this.decodeHtml(rawHtml);

        const wrapper = document.createElement("li");
        wrapper.className = "product-grid__item product-grid__item--merchandise";
        wrapper.innerHTML = decodedHtml;

        this.productGrid?.appendChild(wrapper);
    }

    generateSwatchesMarkup(product, limit = 5, selectedVariantId = "") {
        if (!product || !product.ss_swatches) return "";

        try {
            const { visibleSwatches, activeIndex, remainingCount } = buildRenderableSwatchList(product.ss_swatches, {
                activeFilters: this.activeFilters,
                currentSearchQuery: this.currentSearchQuery,
                enableExpansion: this.enableExpansion,
                limit,
                selectedVariantId,
                colorField: COLOR_FILTER_FIELD,
                origin: window.location.origin,
            });

            if (visibleSwatches.length === 0) return "";

            const escAttr = (s) => String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
            const swatchesHtml = visibleSwatches
                .map((swatch, index) => {
                    const style = swatch.swatch_img
                        ? `background-image: url(${swatch.swatch_img}); background-size: cover;`
                        : `background-color: ${swatch.swatch_hex || "#ccc"};`;

                    const isActive = index === activeIndex;
                    const activeClass = isActive ? "is-active" : "";
                    const isAvailable = isSwatchOperable({ available: swatch.available });
                    const { colorLabel, ariaLabel, ariaChecked, ariaDisabled, tabIndex } = buildSwatchA11yState(swatch.color, {
                        isSelected: isActive,
                        isAvailable,
                    });
                    const ariaDisabledAttr = ariaDisabled ? ` aria-disabled="${ariaDisabled}"` : "";

                    return `
          <button type="button" class="product-card-swatch js-swatch-item ${activeClass}"
               role="radio"
               tabindex="${tabIndex}"
               data-variant-image="${swatch.image}"
               data-variant-hover-image="${swatch.alt}"
               data-variant-sku="${escAttr(swatch.sku || "")}"
               data-variant-url="${localizeUrl(swatch.url)}"
               data-variant-available="${isAvailable ? "true" : "false"}"
               data-color-label="${escAttr(colorLabel)}"
               aria-label="${escAttr(ariaLabel)}"
               aria-checked="${ariaChecked}"
               ${ariaDisabledAttr}
               style="${style}"><strong><span>${escAttr(colorLabel)}</span></strong></button>
        `;
                })
                .join("");

            let remainingHtml =
                remainingCount > 0 ? `<span class="product-card-swatches-remaining">+${remainingCount}</span>` : "";

            return `
        <div class="product-card-swatches-wrapper">
          <div class="product-card-swatches-container" role="radiogroup" aria-label="Choose a color">${swatchesHtml}</div>
          ${remainingHtml}
        </div>
      `;
        } catch (e) {
            console.error("Error parsing swatches:", e);
            return "";
        }
    }

    renderFilters(data) {
        const filtersContainer = this.querySelector(".facets__filters-wrapper");
        if (!filtersContainer || !data.facets) return;

        filtersContainer.innerHTML = "";

        data.facets.forEach((facet) => {
            if (!Array.isArray(facet.values) || facet.values.length <= 1) return;

            const template = this.facetAccordionTemplate.innerHTML;
            const facetName = facet.field.replace(/\./g, "-");
            const filterParamName = `filter-${facetName}`;
            const accordionId = `Facet-Details-${facetName}`;
            const facetLabel = facet.label;

            const accordionHtml = template
                .replace(/\${accordionId}/g, accordionId)
                .replace(/\${filterParamName}/g, filterParamName)
                .replace(/\${facetName}/g, facetName)
                .replace(/\${facetLabel}/g, facetLabel);

            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = accordionHtml;
            const accordionElement = tempDiv.firstElementChild;

            accordionElement.setAttribute("data-facet-field", facet.field);

            const floatingPanel = accordionElement.querySelector("floating-panel-component");

            if (this.tooltipData && floatingPanel) {
                const match = Object.values(this.tooltipData).find((entry) => {
                    return (
                        entry &&
                        entry.key &&
                        entry.key.toLowerCase() === facetLabel.toLowerCase() &&
                        entry.type === "Facet Heading" &&
                        entry.facet === facet.field
                    );
                });

                if (match) {
                    const btn = this.createTooltipButton("Facet Heading", match.label);
                    floatingPanel.prepend(btn);
                    btn.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.toggleTooltip(btn, match.content);
                    });
                }
            }

            const optionsList = accordionElement.querySelector(".facets__inputs-list");
            optionsList.setAttribute("name", facet.label);

            const limit = facet.field === "variant_mfield_custom_main_color" ? 12 : 6;
            const isSwatch = facet.field === "variant_mfield_custom_main_color";
            const hasMore = facet.values.length > limit;

            facet.values.forEach((option, index) => {
                this.#renderFacetOption(facet, option, index, { limit, isSwatch, optionsList });
            });

            if (hasMore) {
                const showMoreBtn = document.createElement("button");
                const showAllText = Theme.translations?.show_all || "Show All";
                const showLessText = Theme.translations?.show_less || "Show Less";

                showMoreBtn.className = "button-unstyled facet-show-more";
                showMoreBtn.type = "button";
                showMoreBtn.innerHTML = `${showAllText} ${facet.values.length}`;

                showMoreBtn.addEventListener("click", () => {
                    const isExpanded = showMoreBtn.getAttribute("aria-expanded") === "true";
                    const hiddenItems = optionsList.querySelectorAll("[data-collapsible-filter]");

                    hiddenItems.forEach((item) => item.classList.toggle("hide"));
                    showMoreBtn.setAttribute("aria-expanded", !isExpanded);
                    showMoreBtn.innerHTML = isExpanded ? `${showAllText} ${facet.values.length}` : `${showLessText}`;
                });

                optionsList.after(showMoreBtn);
            }

            filtersContainer.appendChild(accordionElement);
        });

        // Sync checkboxes with activeFilters after rendering
        this.syncFilterCheckboxes();
    }

    /**
     * Creates and appends a single facet checkbox option to the facet options list.
     * Handles standard value options and range options, wires tooltip buttons for
     * filter-value tooltips, applies colour swatch styling for colour facets, and
     * hides options beyond the visible limit (revealed by the show-more button in renderFilters).
     * @param {Object} facet - The parent facet object from the API response
     * @param {Object} option - The individual facet value to render
     * @param {number} index - Zero-based position within facet.values
     * @param {{ limit: number, isSwatch: boolean, optionsList: Element }} opts
     */
    #renderFacetOption(facet, option, index, { limit, isSwatch, optionsList }) {
        const optionClone = this.facetCheckboxTemplate.content.cloneNode(true);
        const checkbox = optionClone.querySelector("input");
        const labelText = optionClone.querySelector(".checkbox__label-text");
        const labelElement = optionClone.querySelector("label");
        const listItem = optionClone.querySelector(".facets__inputs-list-item");

        const safeValue = option.value !== undefined ? String(option.value) : `${option.low}-${option.high}`;
        const filterId = `filter.${facet.field}.${safeValue.replace(/\s+/g, "-")}`;

        checkbox.name = `filter.${facet.field}`;
        checkbox.value = safeValue;
        checkbox.id = filterId;
        checkbox.checked = option.active;

        if (option.type === "range") {
            checkbox.dataset.low = option.low;
            checkbox.dataset.high = option.high;
            checkbox.dataset.isRange = "true";
        }

        if (labelElement) labelElement.setAttribute("for", filterId);
        if (labelText) labelText.textContent = `${option.label} (${option.count})`;

        if (labelElement) {
            labelElement.setAttribute("tabindex", "0");
            labelElement.addEventListener("keydown", (e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                checkbox.click();
            });
        }

        if (this.tooltipData && labelElement) {
            const matchKey = safeValue.toLowerCase().replace(/\s+/g, "-");
            const match = Object.values(this.tooltipData).find(
                (entry) =>
                    entry &&
                    entry.key &&
                    (entry.key.toLowerCase() === safeValue.toLowerCase() || entry.key.toLowerCase() === matchKey) &&
                    entry.type === "Filter Value" &&
                    entry.facet === facet.field,
            );

            if (match) {
                const btn = this.createTooltipButton("Filter Value");
                labelElement.appendChild(btn);
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggleTooltip(btn, match.content);
                });
            }
        }

        if (isSwatch) {
            const swatchElement = document.createElement("span");
            const customColor = Theme.colorData[option.value];

            swatchElement.className = "color-swatch";

            if (customColor) {
                if (customColor.swatch) {
                    swatchElement.style.backgroundImage = `url(${customColor.swatch})`;
                    swatchElement.style.backgroundSize = "cover";
                    swatchElement.classList.add("has-image");
                } else if (customColor.color) {
                    swatchElement.style.backgroundColor = customColor.color;
                }
            } else {
                swatchElement.style.backgroundColor = option.value;
            }

            labelText.before(swatchElement);
            listItem.classList.add("is-swatch");
        }

        checkbox.addEventListener("change", () => this.handleFilterChange());
        checkbox.addEventListener("keydown", (e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            checkbox.click();
        });

        if (index >= limit) {
            listItem.classList.add("hide");
            listItem.setAttribute("data-collapsible-filter", "true");
        }

        optionsList.appendChild(optionClone);
    }

    syncFilterCheckboxes() {
        if (!this.activeFilters) return;

        const activeFiltersParams = new URLSearchParams(this.activeFilters);
        const checkboxes = this.querySelectorAll(".facets__form-wrapper .checkbox__input");

        checkboxes.forEach((checkbox) => {
            const filterName = checkbox.name;
            const filterValue = checkbox.value;

            if (checkbox.dataset.isRange === "true") {
                // Handle range filters
                const lowValue = checkbox.dataset.low;
                const highValue = checkbox.dataset.high;
                let isActive = false;

                if (activeFiltersParams.has(`${filterName}.low`) && activeFiltersParams.has(`${filterName}.high`)) {
                    const activeLow = activeFiltersParams.get(`${filterName}.low`);
                    const activeHigh = activeFiltersParams.get(`${filterName}.high`);
                    isActive = activeLow === lowValue && activeHigh === highValue;
                }

                checkbox.checked = isActive;
            } else {
                // Handle regular filters
                const activeValues = activeFiltersParams.getAll(filterName);
                const shouldBeChecked = activeValues.includes(filterValue);
                checkbox.checked = shouldBeChecked;
            }
        });
    }

    renderActiveFilters(data) {
        const container = this.querySelector(".js-active-filters-container");
        if (!container || !data.facets) return;

        container.innerHTML = "";
        let hasActive = false;

        data.facets.forEach((facet) => {
            facet.values.forEach((value) => {
                if (value.active) {
                    hasActive = true;
                    const filterBtn = document.createElement("button");
                    filterBtn.className = "active-filter-pill";
                    filterBtn.innerHTML = `${value.label} <span class="close-icon"></span>`;

                    filterBtn.addEventListener("click", () => {
                        let checkbox;
                        if (value.type === "range") {
                            checkbox = this.querySelector(
                                `input[name="filter.${facet.field}"][data-low="${value.low}"][data-high="${value.high}"]`,
                            );
                        } else {
                            checkbox = this.querySelector(`input[name="filter.${facet.field}"][value="${value.value}"]`);
                        }
                        if (checkbox) {
                            checkbox.checked = false;
                            this.handleFilterChange();
                        }
                    });
                    container.appendChild(filterBtn);
                }
            });
        });

        if (hasActive) {
            const clearAllBtn = document.createElement("button");
            clearAllBtn.className = "clear-all-link";
            clearAllBtn.textContent = "Clear All";
            clearAllBtn.addEventListener("click", () => this.clearAllFilters());
            container.appendChild(clearAllBtn);
        }
    }

    initTooltipContainer() {
        const container = this.querySelector(".facets-block-wrapper") || this;
        if (!container) return;
        this.tooltipContainer = document.createElement("div");
        this.tooltipContainer.className = "js-global-tooltip-container facets-tooltip-container";
        this.tooltipContainer.style.display = "none";
        this.tooltipContainer.style.position = "absolute";
        this.tooltipContainer.style.zIndex = "1000";
        container.appendChild(this.tooltipContainer);
    }

    createTooltipButton(type = "Filter Value", label = "") {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `js-tooltip-trigger tooltip-trigger-btn tooltip-trigger-btn--${type.toLowerCase().replace(/\s+/g, "-")}`;

        let innerHTML = "";

        innerHTML += `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_15703_15803)"><path d="M10.0003 5.41663V5.42496M10.0003 8.74996V14.1666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z" stroke="#1B1712" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_15703_15803"><rect width="20" height="20" fill="white"/></clipPath></defs></svg>`;

        if (type === "Facet Heading" && label) {
            innerHTML += `<span>${label}</span>`;
        }

        btn.innerHTML = innerHTML;
        btn.setAttribute("aria-label", `View information about ${label || "this filter"}`);
        return btn;
    }

    toggleTooltip(trigger, content) {
        if (this.activeTooltipTrigger === trigger) {
            this.closeTooltip();
            return;
        }

        this.tooltipContainer.innerHTML = content;
        this.tooltipContainer.style.display = "block";
        this.activeTooltipTrigger = trigger;

        this.positionTooltip(trigger);

        document.addEventListener("click", this.handleOutsideTooltipClickBound);
    }

    positionTooltip(trigger) {
        const triggerRect = trigger.getBoundingClientRect();
        const container = this.querySelector(".facets-block-wrapper") || this;
        const containerRect = container.getBoundingClientRect();
        const tooltipRect = this.tooltipContainer.getBoundingClientRect();

        let top = triggerRect.top - containerRect.top - tooltipRect.height - 10;
        let left = triggerRect.left - containerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

        if (top < 0) {
            top = triggerRect.bottom - containerRect.top + 10;
        }

        if (left < 0) {
            left = 5;
        } else if (left + tooltipRect.width > containerRect.width) {
            left = containerRect.width - tooltipRect.width - 5;
        }

        this.tooltipContainer.style.top = `${top}px`;
        this.tooltipContainer.style.left = `${left}px`;
    }

    closeTooltip() {
        if (!this.tooltipContainer) return;
        this.tooltipContainer.style.display = "none";
        this.activeTooltipTrigger = null;
        document.removeEventListener("click", this.handleOutsideTooltipClickBound);
    }

    handleOutsideTooltipClick(e) {
        if (
            this.tooltipContainer &&
            !this.tooltipContainer.contains(e.target) &&
            !this.activeTooltipTrigger.contains(e.target)
        ) {
            this.closeTooltip();
        }
    }

    clearAllFilters() {
        this.activeFilters = "";
        this.#setCurrentPage(1);

        const checkboxes = this.querySelectorAll(".facets__form-wrapper .checkbox__input");
        checkboxes.forEach((cb) => {
            cb.checked = false;
        });

        const inputs = this.querySelectorAll('.facets__form-wrapper input[type="number"]');
        inputs.forEach((input) => (input.value = ""));

        this.updateBrowserURL();
        this.loadFilteredResults();
    }

    fillOrHide(parent, selector, html) {
        if (!parent) return;

        const elements = parent.querySelectorAll(selector);
        if (elements.length === 0) return;

        elements.forEach((element) => {
            if (!html || html.trim() === "") {
                if (selector === ".js-load-more") {
                    element.classList.add("hidden");
                } else {
                    element.remove();
                }
            } else {
                element.innerHTML = html;
            }
        });
    }

    handleNoResults() {
        this.clearNoResultsMessage();
        if (!this.productGrid || !this.filtersParent) return;
        this.productGrid.innerHTML = "";
        this.filtersParent.classList.add("filters-no-results");
        this.classList.add("is-loaded");
    }

    clearNoResultsMessage() {
        this.filtersParent?.classList.remove("filters-no-results");
    }
    /**
     * Applies storefront promo pricing via the shared promo-pricing utility.
     * Returns liveData with price/compareAtPrice adjusted, or unchanged if no promo applies.
     */
    #applyCampaignPricing(liveData, productTags, productCollections = [], productId = null) {
        if (!liveData?.price?.amount) return liveData;

        const collectionIds = [...(productCollections || [])];
        const currentCollectionId = window.OtterVars?.currentCollectionId;
        if (currentCollectionId) collectionIds.push(currentCollectionId);

        const promo = getProductPromo(
            {
                productId,
                tags: productTags || [],
                collectionIds,
                isGwp: String(liveData.isGwp?.value) === "true",
            },
            liveData.price.amount
        );

        if (!promo) return liveData;

        return {
            ...liveData,
            price: { ...liveData.price, amount: promo.discounted.toFixed(2) },
            compareAtPrice: { ...(liveData.compareAtPrice || {}), amount: promo.original.toFixed(2) },
        };
    }

    #promoCollectionIds(liveData, renderItem) {
        const fromQuery = liveData?.product?.collections?.nodes?.map((node) => node.id) || [];
        return fromQuery.length ? fromQuery : renderItem?.collectionIds || [];
    }
    generateLivePriceMarkup(liveData) {
        if (!liveData || !liveData.price || !liveData.price.amount) return "";

        const price = parseFloat(liveData.price.amount);
        const compareAtPrice = liveData.compareAtPrice ? parseFloat(liveData.compareAtPrice.amount) : null;
        const priceFormatted = formatMoney(price, this.currencySettings);

        // Render the compare-at price and regular price if on sale
        if (compareAtPrice && compareAtPrice > price) {
            const comparePriceFormatted = formatMoney(compareAtPrice, this.currencySettings);
            return `
        <span class="compare-at-price">${comparePriceFormatted}</span>
        <span class="price">${priceFormatted}</span>
      `;
        }

        // Render the standard price regardless of inventory status
        return `<span class="price">${priceFormatted}</span>`;
    }

    generatePromoMarkup(product) {
        const hasPriceReduction = product.msrp && Number(product.msrp) > Number(product.price);

        if (!hasPriceReduction) {
            return "";
        }

        if (!product.tags || !Array.isArray(product.tags)) return "";

        const promoTags = product.tags.filter((tag) => tag.startsWith("promo:")).slice(0, 3);

        if (!promoTags.length) return "";

        return promoTags
            .map((tag) => {
                const parts = tag.split(":");

                if (parts.length >= 3) {
                    const promoStyle = parts[1].trim();
                    const promoLabel = parts[2].trim();

                    return `<span class="product-card-item__promo-badge product-card-item__promo-badge--${promoStyle}">${promoLabel}</span>`;
                }

                return "";
            })
            .join("");
    }

    generateBadgeMarkup(product, selectedSwatch, renderItem) {
        const hasThreshold = this.dataset.trendingThreshold !== undefined && this.dataset.trendingThreshold !== "";
        const threshold = Number(this.dataset.trendingThreshold);
        const rawPageViews = [
            selectedSwatch?.variant_mfield_custom_page_views,
            renderItem?.swatch?.variant_mfield_custom_page_views,
            renderItem?.variant_mfield_custom_page_views // Added fallback for non-swatch products
        ].find((value) => value !== undefined && value !== null && value !== "");
        const pageViews = Number(Array.isArray(rawPageViews) ? rawPageViews[0] : rawPageViews ?? 0);

        const statusBadgeMarkup = this.generateStatusBadgeMarkup(product);
        const trendingBadgeMarkup = hasThreshold && pageViews > threshold
            ? `<span class="product-card-item__badge product-card-item__badge--trending">${this.dataset.trendingLabel || "Trending"}</span>`
            : "";

        return `${trendingBadgeMarkup}${statusBadgeMarkup}`;
    }

    generateStatusBadgeMarkup(product) {
        if (!product.tags || !Array.isArray(product.tags)) return "";

        const statusTag = product.tags.find((tag) => tag.startsWith("status:"));

        if (statusTag) {
            const parts = statusTag.split(":");

            if (parts.length >= 3) {
                const badgeStyle = parts[1].trim();
                const badgeLabel = parts[2].trim();

                return `<span class="product-card-item__badge product-card-item__badge--${badgeStyle}">${badgeLabel}</span>`;
            }
        }

        return "";
    }

    generateRatingMarkup(product) {
        const noRating = Theme.translations?.no_rating || "Be the first to review this product";
        const productId = product.uid || product.id;

        return `
      <div class="product-card-item__rating">
        <div class="yotpo-widget-instance"
             data-yotpo-instance-id="${this.yotpoWidgetId}"
             data-yotpo-product-id="${productId}">
        </div>

        <div class="product-card-item__rating product-card-item__rating--empty"><p>${noRating}</p></div>
      </div>`;
    }

    syncCardAvailabilityState(card, isAvailable) {
        if (!card) return;

        card.classList.toggle("is-not-available", !isAvailable);
    }

    getPriceAnnouncementText(priceContainer) {
        if (!priceContainer) return "";

        return priceContainer.textContent?.replace(/\s+/g, " ").trim() || "";
    }

    getSwatchAnnouncementTarget(card) {
        if (!card) return null;

        let announcer = card.querySelector("[data-swatch-update-announcer]");

        if (announcer) {
            return announcer;
        }

        announcer = document.createElement("div");
        announcer.className = "visually-hidden";
        announcer.setAttribute("role", "status");
        announcer.setAttribute("aria-live", "polite");
        announcer.setAttribute("aria-atomic", "true");
        announcer.setAttribute("data-swatch-update-announcer", "true");

        const swatchContainer = card.querySelector(".product-card-item__swatches") || card;
        swatchContainer.appendChild(announcer);

        return announcer;
    }

    announceSwatchUpdate(card, message) {
        const announcer = this.getSwatchAnnouncementTarget(card);

        if (!announcer || !message) return;

        announcer.textContent = "";
        requestAnimationFrame(() => {
            announcer.textContent = message;
        });
    }

    getLoadMoreAnnouncementTarget() {
        let announcer = this.querySelector("[data-load-more-announcer]");

        if (announcer) {
            return announcer;
        }

        announcer = document.createElement("div");
        announcer.className = "visually-hidden";
        announcer.setAttribute("role", "status");
        announcer.setAttribute("aria-live", "polite");
        announcer.setAttribute("aria-atomic", "true");
        announcer.setAttribute("data-load-more-announcer", "true");
        this.appendChild(announcer);

        return announcer;
    }

    announceAppendedResults(resultCount) {
        const message = buildAppendedResultsAnnouncement(resultCount);
        const announcer = this.getLoadMoreAnnouncementTarget();

        if (!announcer || !message) return;

        announcer.textContent = "";
        requestAnimationFrame(() => {
            announcer.textContent = message;
        });
    }

    focusFirstAppendedResult(appendedCards = []) {
        const firstAppendedCard = appendedCards[0];

        if (!firstAppendedCard) return;

        const focusTarget = firstAppendedCard.querySelector(".product-card-item__title a") || firstAppendedCard;

        if (focusTarget === firstAppendedCard && !focusTarget.hasAttribute("tabindex")) {
            focusTarget.setAttribute("tabindex", "-1");
        }

        requestAnimationFrame(() => {
            focusTarget.focus();
        });
    }

    handleSwatchKeydown(event) {
        const key = event.key;

        if (!["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End", "Enter", " "].includes(key)) {
            return;
        }

        const swatches = Array.from(
            event.currentTarget.closest(".product-card-swatches-container")?.querySelectorAll(".js-swatch-item") || [],
        );

        if (swatches.length === 0) {
            return;
        }

        const currentIndex = swatches.indexOf(event.currentTarget);

        if (key === "Enter" || key === " ") {
            event.preventDefault();

            if (isSwatchOperable(event.currentTarget)) {
                event.currentTarget.click();
            }

            return;
        }

        const nextIndex = getNextOperableSwatchIndex(swatches, currentIndex, key);

        if (nextIndex === -1) {
            event.preventDefault();
            return;
        }

        const nextSwatch = swatches[nextIndex];

        if (!nextSwatch) {
            return;
        }

        event.preventDefault();
        nextSwatch.focus();

        if (nextSwatch !== event.currentTarget) {
            nextSwatch.click();
        }
    }

    updateSwatchSelectionState(swatches, activeSwatch) {
        swatches.forEach((swatchButton) => {
            const isSelected = swatchButton === activeSwatch;
            const { ariaLabel, ariaChecked, ariaDisabled, tabIndex } = buildSwatchA11yState(swatchButton.dataset.colorLabel, {
                isSelected,
                isAvailable: swatchButton.dataset.variantAvailable === "true",
            });

            swatchButton.classList.toggle("is-active", isSelected);
            swatchButton.classList.toggle("is-unavailable", swatchButton.dataset.variantAvailable === "false");
            swatchButton.setAttribute("aria-label", ariaLabel);
            swatchButton.setAttribute("aria-checked", ariaChecked);
            swatchButton.setAttribute("tabindex", tabIndex);

            if (ariaDisabled) {
                swatchButton.setAttribute("aria-disabled", ariaDisabled);
            } else {
                swatchButton.removeAttribute("aria-disabled");
            }
        });
    }

    refreshYotpo() {
        requestAnimationFrame(() => {
            if (window.yotpo && typeof window.yotpo.refreshWidgets === "function") {
                window.yotpo.refreshWidgets();
            } else if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === "function") {
                window.yotpoWidgetsContainer.initWidgets();
            }
        });
    }

    #getOrSetCookie(name) {
        let value = document.cookie
            .split("; ")
            .find((row) => row.startsWith(`${name}=`))
            ?.split("=")[1];
        if (!value) {
            value = crypto.randomUUID?.() || Math.random().toString(36).substring(2);
            document.cookie = `${name}=${value}; path=/; max-age=31536000`;
        }
        return value;
    }

    async loadNextPage(options = {}) {
        const { auto = false } = options;

        if (this.loadMoreBtn?.classList.contains("is-loading")) return;

        try {
            this.startUpdating();
            this.loadMoreBtn?.classList.add("is-loading");

            const batch = await this.searchspringModel.takeNextRenderBatch({
                batchSize: DEFAULT_BATCH_SIZE,
                requestOptions: this.getSearchspringRequestParams(),
                renderContext: this.#buildModelRenderContext(),
            });

            if (batch.length === 0) {
                this.updateLoadMoreVisibility();
                this.stopUpdating();
                return;
            }

            const appendedCards = await this.renderProductBatch(batch, true);

            if (auto) {
                this.autoLoadCount += 1;
            }

            this.updateLoadMoreVisibility();
            this.announceAppendedResults(appendedCards.length);

            if (!auto) {
                this.focusFirstAppendedResult(appendedCards);
            }

            this.updateBrowserURL();
            this.#setupInfiniteScroll();
        } catch (error) {
            console.error("Error loading more products:", error);
            this.stopUpdating();
        } finally {
            this.loadMoreBtn?.classList.remove("is-loading");
        }
    }

    #setupInfiniteScroll() {
        if (!this.loadMoreBtn) return;

        if (this.observer) {
            this.observer.disconnect();
        }

        if (!this.hasMoreRenderableResults() || this.autoLoadCount >= this.autoLoadLimit) {
            return;
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;

                if (
                    shouldAutoLoadNextBatch({
                        autoLoadCount: this.autoLoadCount,
                        autoLoadLimit: this.autoLoadLimit,
                        isIntersecting: entry?.isIntersecting,
                        isLoading: this.loadMoreBtn?.classList.contains("is-loading"),
                    })
                ) {
                    this.hasAutoLoaded = true;
                    this.observer.unobserve(this.loadMoreBtn);
                    this.loadNextPage({ auto: true });
                }
            },
            {
                rootMargin: "0px 0px 200px 0px",
            },
        );

        this.observer.observe(this.loadMoreBtn);
    }

    handleFilterChange() {
        const params = new URLSearchParams();
        const checkboxes = this.querySelectorAll(".facets__form-wrapper .checkbox__input:checked");

        checkboxes.forEach((cb) => {
            if (cb.dataset.isRange === "true") {
                if (cb.dataset.low) params.append(`${cb.name}.low`, cb.dataset.low);
                if (cb.dataset.high) params.append(`${cb.name}.high`, cb.dataset.high);
            } else {
                params.append(cb.name, cb.value);
            }
        });

        this.activeFilters = params.toString();
        this.resetBatchRenderState();
        this.#setCurrentPage(1);

        this.updateBrowserURL();
        this.loadFilteredResults();
    }

    async loadFilteredResults() {
        try {
            await this.#fetchAndRenderPage(1, { withFilters: true });
        } catch (error) {
            console.error("Searchspring Update Error:", error);
            this.stopUpdating();
        }
    }

    /**
     * Fetches a single page from the Searchspring API, renders the products, and
     * optionally re-renders filters and/or sorting. Fires the 'render' beacon and
     * (re)initialises infinite scroll. Shared by loadFilteredResults and #handleSortChange.
     * @param {number} [page=1] - The page to fetch
     * @param {{ withFilters?: boolean, withSorting?: boolean }} [options]
     * @returns {Promise<Object|null>} The API response, or null if the request was aborted
     */
    async #fetchAndRenderPage(page = 1, { withFilters = false, withSorting = false } = {}) {
        const data = await this.searchspringModel.getProductsData(page, this.getSearchspringRequestParams(page));

        if (!data) {
            this.stopUpdating();
            return null;
        }

        await this.renderProducts(data, false);

        if (withFilters) {
            this.renderFilters(data);
            this.renderActiveFilters(data);
        }

        if (withSorting) {
            this.renderSorting(data);
        }

        this.#sendBeaconEvent("render", { responseId: this.currentResponseId });
        this.#setupInfiniteScroll();

        return data;
    }

    updateBrowserURL() {
        const nextUrl = this.searchspringModel.buildBrowserUrl({
            href: window.location.href,
            currentSearchQuery: this.currentSearchQuery,
            activeFilters: this.activeFilters,
            currentPage: this.currentPage,
            filterFieldToUrl: SearchspringListingBase.filterFieldToUrl,
        });

        window.history.pushState({}, "", nextUrl);
    }

    startUpdating() {
        this.classList.add("is-updating");
    }

    stopUpdating() {
        this.classList.remove("is-updating");
    }

    highlightText(text, query) {
        if (!query || !text) return text;

        const escapedQuery = query.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
        const regex = new RegExp(`(${escapedQuery})`, "gi");

        return text.replace(regex, '<mark class="ss-highlight">$1</mark>');
    }

    generateSrcset(url) {
        if (!url || url.includes('data:image')) return '';

        const baseUrl = url.split('?')[0];
        const widths = [200, 400, 600, 800, 1000];

        return widths
            .map(width => `${baseUrl}?width=${width}&format=webp&quality=90 ${width}w`)
            .join(', ');
    }

    #sendBeaconEvent(event, data, keepAlive = false) {
        if (!this.siteId) return;
        // using sendBeacon() causes issues with CORS due to "Access-Control-Allow-Origin : *" header in the Searchspring Server response
        this.searchspringTrackingService.sendEvent({
            siteId: this.siteId,
            event,
            data,
            keepAlive,
            beaconPath: this.getBeaconPath(),
            pageUrl: window.location.href,
            userId: this.#getOrSetCookie("ssUserId"),
            sessionId: this.#getOrSetCookie("ssSessionId"),
            pageLoadId: this.pageLoadId,
        });
    }

    #setupImpressionTracking() {
        // uses pendingResults and flush() to group all elements that became visible and send them as one request.

        if (!this.currentResponseId || !this.productGrid) return;

        if (this.impressionObserver) this.impressionObserver.disconnect();

        const pendingResults = [];
        let flushScheduled = false;

        const flush = () => {
            if (pendingResults.length === 0 || !this.currentResponseId) return;
            const results = [...pendingResults];
            pendingResults.length = 0;
            this.#sendBeaconEvent("impression", {
                responseId: this.currentResponseId,
                results,
                banners: [],
            });
        };

        this.impressionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    const card = entry.target;
                    const impressionKey = this.searchspringTrackingService.getImpressionKey(card);
                    const result = this.searchspringTrackingService.buildTrackingResultFromElement(card);
                    if (!result?.uid || !impressionKey || this.trackedImpressions.has(impressionKey)) return;

                    this.trackedImpressions.add(impressionKey);
                    this.impressionObserver.unobserve(card);

                    pendingResults.push(result);

                    if (!flushScheduled) {
                        flushScheduled = true;
                        requestAnimationFrame(() => {
                            flushScheduled = false;
                            flush();
                        });
                    }
                });
            },
            { threshold: 0.5 },
        );

        this.productGrid.querySelectorAll("product-card").forEach((card) => {
            const impressionKey = card.getAttribute("data-impression-key") || card.getAttribute("data-product-id");
            if (impressionKey && !this.trackedImpressions.has(impressionKey)) {
                this.impressionObserver.observe(card);
            }
        });
    }

    #handleCartUpdate(e) {
        if (!this.currentResponseId || e.detail?.data?.didError) return;

        const variantId = e.detail?.sourceId;
        if (!variantId) return;

        const productInfo = this.productDataMap.get(String(variantId));
        if (!productInfo) return;

        const qty = e.detail?.data?.itemCount ?? 1;
        const result = this.searchspringTrackingService.buildAddToCartResult(productInfo, qty);

        if (!result) return;

        this.#sendBeaconEvent("addtocart", {
            responseId: this.currentResponseId,
            results: [result],
        });
    }
}
